import Loader from "components/DesignSystem/Loader/loader";
import {
  CODE_RECEIVED,
  CONNECTION_AUTHENTICATION_FAILED,
  CONNECTION_AUTHENTICATION_SUCCESS,
} from "constants/dataSource";
import { ConnectionLocalState } from "hooks/useBankConnect";
import { useQuery } from "hooks/useQuery";
import { useToast } from "hooks/useToast";
import { useCallback, useEffect, useRef } from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import { useConfirmConnectionMutation } from "store/apis/booksConnections";
import { BackendError } from "types/utils/error";
import { capitalizeFirstLetter } from "utils/capitalizeFirstLetter";

export const ConnectionConfirmation = () => {
  const [confirmConnection, { isLoading: isConfirming }] =
    useConfirmConnectionMutation();
  const history = useHistory();
  const query = useQuery();
  const { bank } = useParams<{ bank: string }>();
  const { alertToast, successToast } = useToast();
  const ref = useRef<string | null>(null);

  const code = query.get("code");
  const state = query.get("state");
  const error = query.get("error");
  const errordescription = query.get("error_description");

  const authenticateCode = useCallback(
    async ({
      groupId,
      entityId,
      connectionId,
      txn_pull_choice,
      txn_pull_date,
      code,
    }: {
      groupId: string;
      entityId: string;
      connectionId: string;
      code: string;
      txn_pull_choice: string;
      txn_pull_date: string;
    }) => {
      if (isConfirming) {
        return;
      }

      try {
        return await confirmConnection({
          groupId,
          connectionId,
          entityId,
          code,
          txn_pull_choice,
          txn_pull_date,
        }).unwrap();
      } catch (error) {
        throw new Error((error as BackendError).data?.error?.message);
      }
    },
    []
  );

  useEffect(() => {
    const authenticate = async (code: string) => {
      const stateKey = localStorage.getItem("connection_oauth_state_key");

      try {
        if (stateKey && state === stateKey) {
          const localState = localStorage.getItem(stateKey);

          if (localState) {
            const localStateObj = JSON.parse(
              localState
            ) as ConnectionLocalState;

            await authenticateCode({
              ...localStateObj,
              code,
            });
            successToast({
              message: `${capitalizeFirstLetter(bank)} is Connected`,
            });

            localStorage.removeItem("connection_oauth_state_key");

            if (localStateObj?.invokedFrom) {
              history.replace(localStateObj?.invokedFrom);
              localStorage.removeItem(stateKey);
              return;
            }

            // Use localStorage for cross-window communication (works even after cross-origin OAuth redirects)
            // Also try postMessage as fallback
            localStorage.setItem(
              "connection_popup_result",
              JSON.stringify({ status: CONNECTION_AUTHENTICATION_SUCCESS })
            );
            window.opener?.postMessage(CONNECTION_AUTHENTICATION_SUCCESS, "*");
            window.close();
            return;
          }
        }

        throw new Error("Failed to authenticate, please contact support!");
      } catch (error: any) {
        alertToast({ message: error.message });
        if (stateKey && state === stateKey) {
          const localState = localStorage.getItem(stateKey);

          if (localState) {
            const localStateObj = JSON.parse(
              localState
            ) as ConnectionLocalState;

            if (localStateObj?.invokedFrom) {
              history.replace(localStateObj?.invokedFrom);
            }
          }
          localStorage.setItem(
            "connection_popup_result",
            JSON.stringify({ status: CONNECTION_AUTHENTICATION_FAILED })
          );
          window.opener?.postMessage(CONNECTION_AUTHENTICATION_FAILED);
        }
      }
    };

    const redirectStore = localStorage.getItem("redirect_store");

    const returnWithoutExchange = redirectStore
      ? JSON.parse(redirectStore)?.connection_confirmation_config
          ?.return_code_without_exchange
      : false;

    if (ref.current) {
      return;
    }
    ref.current = "RAN";

    if (error) {
      // OAuth failed before we got a code. Notify the parent so its connect
      // promise rejects and the modal clears its connecting state + toasts the
      // reason. The error screen below still renders for the full-page flow.
      localStorage.setItem(
        "connection_popup_result",
        JSON.stringify({
          status: CONNECTION_AUTHENTICATION_FAILED,
          message: errordescription || error,
        })
      );
      window.opener?.postMessage(CONNECTION_AUTHENTICATION_FAILED, "*");
      localStorage.removeItem("redirect_store");
      return;
    }

    if (returnWithoutExchange && code) {
      // Use localStorage for cross-window communication (works even after cross-origin OAuth redirects)
      // Also try postMessage as fallback
      localStorage.setItem(
        "connection_popup_result",
        JSON.stringify({ status: CODE_RECEIVED, code })
      );
      window.opener?.postMessage(`${CODE_RECEIVED}:${code}`);
      localStorage.removeItem("redirect_store");
      // Close the popup ourselves so the flow never gets stuck on the loader.
      // The opener is lost after the cross-origin OAuth redirect, so it can't
      // always close us; the code is already handed off via localStorage above.
      window.close();
      return;
    }

    if (code && !returnWithoutExchange) {
      authenticate(code);
    }
  }, [authenticateCode, code, state, error, errordescription]);

  return (
    <div className="t-h-screen t-w-screen t-flex t-justify-center t-items-center">
      {error ? (
        <>
          <div className="t-m-5">
            <p className="">Error happened</p>
            <p>{error}</p>
            <p>{errordescription}</p>
            <Link to="/books/data-sources">Go to dashboard</Link>
          </div>
        </>
      ) : (
        <>
          <div className="t-animate-spin">
            <Loader size="small" />
          </div>
          <p className="t-m-5">Confirming integration with {bank}...</p>
        </>
      )}
    </div>
  );
};
